import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const logout = useCallback(() => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setIsAuthenticated(false);
  }, []);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/admin/verify`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          setIsAuthenticated(true);
        } else {
          logout();
          if (response.status === 401) {
            toast.error('Session expired. Please log in again.');
          }
        }
      } catch (error) {
        console.error('Token verification failed:', error);
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };

    verifyToken();
  }, [token, API_URL, logout]);

  const login = async (password) => {
    const response = await fetch(`${API_URL}/api/admin/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ password })
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Invalid credentials');
    }

    const data = await response.json();
    localStorage.setItem('admin_token', data.access_token);
    setToken(data.access_token);
    setIsAuthenticated(true);
    return true;
  };

  // Wrapper for authenticated API calls that handles 401 automatically
  const authFetch = useCallback(async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
      },
    });

    if (response.status === 401) {
      logout();
      toast.error('Session expired. Please log in again.');
      throw new Error('Unauthorized');
    }

    return response;
  }, [token, logout]);

  return (
    <AuthContext.Provider value={{ token, isAuthenticated, isLoading, login, logout, authFetch }}>
      {children}
    </AuthContext.Provider>
  );
};
